import type { DailyWorkerRatesSnapshot } from './types';

export interface DailyWorkerDayResult {
  incomeTax: number;
  localTax: number;
  workerEmployment: number;
  netPay: number;
}

export interface DailyWorkerMonthResult {
  incomeTax: number;
  localTax: number;
  workerEmployment: number;
  netPay: number;
  grossPay: number;
}

export function dailyWorkerForDay(
  dailyWage: number,
  rates: DailyWorkerRatesSnapshot,
): DailyWorkerDayResult {
  const workerEmployment = Math.round(dailyWage * rates.eiWorker);

  if (dailyWage <= rates.dailyExempt) {
    return {
      incomeTax: 0,
      localTax: 0,
      workerEmployment,
      netPay: dailyWage - workerEmployment,
    };
  }

  const taxable = dailyWage - rates.dailyExempt;
  const grossTax = Math.floor(taxable * rates.dailyRate);
  const incomeTax = Math.floor(grossTax * (1 - rates.creditRate));
  const localTax = Math.floor(incomeTax * rates.localTaxRate);
  const netPay = dailyWage - incomeTax - localTax - workerEmployment;

  return { incomeTax, localTax, workerEmployment, netPay };
}

export function dailyWorkerForMonth(
  dailyWage: number,
  workDays: number,
  rates: DailyWorkerRatesSnapshot,
): DailyWorkerMonthResult {
  if (workDays <= 0 || dailyWage <= 0) {
    return {
      incomeTax: 0,
      localTax: 0,
      workerEmployment: 0,
      netPay: 0,
      grossPay: 0,
    };
  }

  let incomeTax = 0;
  let localTax = 0;
  let workerEmployment = 0;

  for (let i = 0; i < workDays; i++) {
    const day = dailyWorkerForDay(dailyWage, rates);
    incomeTax += day.incomeTax;
    localTax += day.localTax;
    workerEmployment += day.workerEmployment;
  }

  const grossPay = dailyWage * workDays;
  return {
    incomeTax,
    localTax,
    workerEmployment,
    netPay: grossPay - incomeTax - localTax - workerEmployment,
    grossPay,
  };
}

export function businessServiceForPayment(payment: number, combinedRate: number) {
  const withholding = Math.round(payment * combinedRate);
  return { withholding, netPay: payment - withholding };
}

export function businessServiceForMonth(
  unitRate: number,
  count: number,
  combinedRate: number,
) {
  if (count <= 0 || unitRate <= 0) {
    return { withholding: 0, netPay: 0, grossPay: 0 };
  }

  let withholding = 0;
  for (let i = 0; i < count; i++) {
    withholding += Math.round(unitRate * combinedRate);
  }
  const grossPay = unitRate * count;
  return { withholding, netPay: grossPay - withholding, grossPay };
}
